import { LanguageService } from '@app/core/services/language.service';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FieldType, FormlyModule, FormlyFieldConfig, FormlyFormBuilder, FormlyConfig } from '@ngx-formly/core';
import { TranslateModule } from '@ngx-translate/core';
import { AbstractControl, AsyncValidatorFn, FormArray, FormGroup, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { CustomFormlyFieldConfig, DefaultValueWithRefObject } from '@app/core/interfaces/formly-forms.interface';
import { DynamicIconComponent } from '@app/shared/components/dynamic-icon';
import { SubSink } from 'subsink';
import { MiscService } from '@app/core/services/misc.service';

@Component({
  selector: 'app-formly-field-repeat',
  templateUrl: './formly-field-repeat.component.html',
  styleUrls: ['./formly-field-repeat.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    TranslateModule,
    ReactiveFormsModule,
    FormlyModule,
    DynamicIconComponent
  ],
})
export class FormlyFieldRepeatComponent extends FieldType implements OnInit, OnDestroy {
  rows: { fields: FormlyFieldConfig[], model: any, form: FormGroup, index: number }[] = [];
  fieldTemplate: any[] = [];
  private subsink = new SubSink();
  private isInitializing = true;
  private previousControl: AbstractControl | null = null;

  constructor(private miscService: MiscService,
              private languageService: LanguageService,
              private cdr: ChangeDetectorRef, 
              private formlyBuilder: FormlyFormBuilder,
              private formlyConfig: FormlyConfig,
  ) {
    super();
  }

  ngOnInit(): void {
    // Set initializing flag IMMEDIATELY to block any stale subscriptions
    this.isInitializing = true;
    
    // Completely unsubscribe and recreate SubSink to ensure no stale subscriptions
    this.subsink.unsubscribe();
    this.subsink = new SubSink();
    
    // Get the field template from fieldArray.fieldGroup
    const fieldArray = typeof this.field.fieldArray === 'function' 
      ? this.field.fieldArray(this.field) 
      : this.field.fieldArray;
    
    if (fieldArray?.fieldGroup) {
      this.fieldTemplate = fieldArray.fieldGroup;
    }

    this.previousControl = this.field.formControl ?? null;

    // Always create a fresh FormArray to avoid stale state reuse between models
    const formArray = new FormArray([]);
    formArray.setValidators(this.composeValidators(this.previousControl?.validator ?? null, this.resolveConfiguredValidators()));
    formArray.setAsyncValidators(this.composeAsyncValidators(this.previousControl?.asyncValidator ?? null, this.resolveConfiguredAsyncValidators()));

    // Replace the form control in parent (or update field reference)
    if (this.field.parent?.formControl && this.field.key) {
      const parent = this.field.parent.formControl as FormGroup;
      const key = this.field.key as string;
      if (parent.get(key)) {
        parent.removeControl(key);
      }
      parent.addControl(key, formArray);
    }
    // Update the field reference
    (this.field as any).formControl = formArray;

    // Get existing data from model
    const existingData = this.miscService.getDeepChildValue(this.model, (this.field.key || this.field.props?.['key']) as string) as any[] | undefined;
    
    // Clear UI rows
    this.rows = [];
    
    // Clear FormArray controls completely - use emitEvent: false to prevent valueChanges
    while (this.formArray.length > 0) {
      this.formArray.removeAt(0, { emitEvent: false });
    }
    
    // Small delay to ensure cleanup completes before repopulating
    setTimeout(() => {
      // Populate FormArray with fresh model data
      if (existingData && existingData.length > 0) {
        existingData.forEach((rowData: any) => {
          const formGroup = new FormGroup({});
          this.formArray.push(formGroup, { emitEvent: false });
        });
        
        // Create UI rows
        for (let i = 0; i < this.formArray.length; i++) {
          const rowData = existingData[i] || {};
          this.createRowUI(rowData, i);
        }
      }
      // If no existing data, add one empty row if configured
      else if (!this.props['hideInitialRow']) {
        this.addRow();
      }
      
      // Subscribe to FormArray changes BEFORE marking initialization complete
      this.subsink.sink = this.formArray.valueChanges.subscribe(values => {
        // Only update model after initialization is complete
        if (!this.isInitializing) {
          this.miscService.setDeepChildValue(this.model, (this.field.key || this.field.props?.['key']) as string, values);
          // Sync UI rows with FormArray controls
          this.syncRowsWithFormArray();
          this.formArray.updateValueAndValidity({ emitEvent: false });
        }
      });
      
      // Mark initialization complete AFTER setting up subscription
      this.isInitializing = false;
      this.formArray.updateValueAndValidity({ emitEvent: false });
      
      this.cdr.markForCheck();
    }, 0);
  }

  ngOnDestroy(): void {
    this.subsink.unsubscribe();
    // Detach control to avoid leaking stale FormArray across model switches
    if (this.field?.parent?.formControl && this.field.key) {
      const parent = this.field.parent.formControl as FormGroup;
      if (parent.get(this.field.key as string)) {
        parent.removeControl(this.field.key as string);
      }
    }
    (this.field as any).formControl = undefined;
  }

  private getModelValue(): any[] | undefined {
    // Get the array value from the model using the field key
    const keys = ((this.field.key as string) || (this.field.props?.['key'] as string)).split('.');
    let value: any = this.model;
    
    for (const key of keys) {
      if (value && typeof value === 'object' && key in value) {
        value = value[key];
      } else {
        return undefined;
      }
    }
    
    return Array.isArray(value) ? value : undefined;
  }

  get formArray(): FormArray {
    return this.formControl as FormArray;
  }

  addRow(): void {
    const rowIndex = this.rows.length;
    const rowModel = {};
    const formGroup = new FormGroup({});
    const rowFields = this.createRowFields(rowModel, formGroup);
    
    // Build the fields using Formly's builder
    this.formlyBuilder.buildForm(formGroup, rowFields, rowModel, this.options);
    
    this.rows.push({ fields: rowFields, model: rowModel, form: formGroup, index: rowIndex });
    this.formArray.push(formGroup);
    this.formArray.markAsDirty();
    this.formArray.markAsTouched();
    this.formArray.updateValueAndValidity({ emitEvent: false });
    
    this.cdr.markForCheck();
  }

  addRowFromData(data: any): void {
    const rowIndex = this.rows.length;
    const rowModel = { ...data };
    const formGroup = new FormGroup({});
    const rowFields = this.createRowFields(rowModel, formGroup);
    
    // Build the fields using Formly's builder
    this.formlyBuilder.buildForm(formGroup, rowFields, rowModel, this.options);
    
    this.rows.push({ fields: rowFields, model: rowModel, form: formGroup, index: rowIndex });
    this.formArray.push(formGroup);
    this.formArray.markAsDirty();
    this.formArray.markAsTouched();
    this.formArray.updateValueAndValidity({ emitEvent: false });
    
    this.cdr.markForCheck();
  }

  removeRow(index: number): void {
    if (index >= 0 && index < this.rows.length) {
      this.rows.splice(index, 1);
      this.formArray.removeAt(index);
      
      // Update indices for remaining rows
      this.rows.forEach((row, idx) => {
        row.index = idx;
      });
      
      this.cdr.markForCheck();
    }
  }

  private createRowUI(data: any, index: number): void {
    // Create UI row for existing FormArray control - don't add to FormArray
    const rowModel = { ...data };
    const formGroup = this.formArray.at(index) as FormGroup;
    const rowFields = this.createRowFields(rowModel, formGroup);
    
    // Build the fields using Formly's builder
    this.formlyBuilder.buildForm(formGroup, rowFields, rowModel, this.options);
    
    this.rows.push({ fields: rowFields, model: rowModel, form: formGroup, index });
  }

  private syncRowsWithFormArray(): void {
    const currentLength = this.formArray.length;
    const rowsLength = this.rows.length;
    
    // If FormArray has more items than UI rows, add missing rows
    if (currentLength > rowsLength) {
      for (let i = rowsLength; i < currentLength; i++) {
        const rowData = this.formArray.at(i).value;
        this.createRowUI(rowData, i);
      }
    }
    // If FormArray has fewer items than UI rows, remove extra rows
    else if (currentLength < rowsLength) {
      this.rows.splice(currentLength, rowsLength - currentLength);
    }
    
    // Update row indices
    this.rows.forEach((row, idx) => {
      row.index = idx;
    });

    this.formArray.markAsDirty();
    this.formArray.markAsTouched();
    this.formArray.updateValueAndValidity({ emitEvent: false });
    
    this.cdr.markForCheck();
  }

  private createRowFields(model: any, form: FormGroup): FormlyFieldConfig[] {
    this.initializeRowModel(model, this.fieldTemplate as CustomFormlyFieldConfig[]);
    
    return this.fieldTemplate.map((templateField: any) => {
      return {
        key: templateField.key,
        type: templateField.type,
        props: { ...(templateField.props || templateField.templateOptions) },
        className: templateField.className,
        defaultValue: templateField.defaultValue,
        expressions: templateField.expressions,
        validators: templateField.validators,
        asyncValidators: templateField.asyncValidators,
        hideExpression: templateField.hideExpression,
      } as FormlyFieldConfig;
    });
  }

  private initializeRowModel(model: any, fields: CustomFormlyFieldConfig[]): void {
    for (const field of fields) {
      if (field.fieldGroup && Array.isArray(field.fieldGroup)) {
        this.initializeRowModel(model, field.fieldGroup);
      }

      this.initializeFieldValue(model, field);

      if (field.fieldArray?.fieldGroup && Array.isArray(field.fieldArray.fieldGroup)) {
        this.initializeRowModel(model, field.fieldArray.fieldGroup);
      }
    }
  }

  private initializeFieldValue(model: any, field: CustomFormlyFieldConfig): void {
    if (typeof field.key !== 'string' || !field.key) {
      return;
    }

    const currentValue = this.miscService.getDeepChildValue(model, field.key);
    if (currentValue !== undefined && currentValue !== null) {
      return;
    }

    if (field.type === 'REPEAT') {
      this.miscService.setDeepChildValue(model, field.key, []);
      return;
    }

    const resolvedDefaultValue = this.resolveFieldDefaultValue(field);
    this.miscService.setDeepChildValue(model, field.key, resolvedDefaultValue !== undefined ? resolvedDefaultValue : null);
  }

  private resolveFieldDefaultValue(field: CustomFormlyFieldConfig): any {
    const defaultValue = field.props?.defaultValue;

    if (defaultValue === undefined) {
      return field.defaultValue;
    }

    if (typeof defaultValue === 'object' && defaultValue !== null && 'fromReferenceObject' in defaultValue) {
      return undefined;
    }

    let resolvedDefaultValue = defaultValue;

    if (resolvedDefaultValue === '{{today}}') {
      resolvedDefaultValue = new Date().toISOString().split('T')[0];
    }
    if (resolvedDefaultValue === '{{now}}') {
      resolvedDefaultValue = new Date().toISOString();
    }
    if (resolvedDefaultValue === '{{current_year}}') {
      resolvedDefaultValue = new Date().getFullYear();
    }
    if (resolvedDefaultValue === '{{next_year}}') {
      resolvedDefaultValue = new Date().getFullYear() + 1;
    }
    if (field.type === 'NUMBER' && typeof resolvedDefaultValue === 'string') {
      resolvedDefaultValue = parseFloat(resolvedDefaultValue);
    }

    if (resolvedDefaultValue && typeof resolvedDefaultValue === 'object') {
      return this.miscService.deepCopy(resolvedDefaultValue);
    }

    return resolvedDefaultValue;
  }

  public hasErrors(field: FormlyFieldConfig): boolean {
    return field.formControl ? field.formControl.invalid && (field.formControl.dirty || field.formControl.touched) : false;
  }

  public hasRepeatErrors(): boolean {
    return this.formArray.invalid && (this.showError || this.formArray.dirty || this.formArray.touched);
  }

  public getErrorMessages = (field: FormlyFieldConfig): string[] => {
    let errorMessages = [];
    if (field.formControl && field.formControl.errors) {
      const keys = Object.keys(field.formControl.errors);
      if (keys.length > 0) {
        for (const key of keys) {
          const errType = field.formControl.errors[key];
          let errText= this.languageService.instant('ERRORS.' + key.toUpperCase());
          if (errType) {
            for (const attr in errType) {
              errText = errText.replace('{{' + attr + '}}', errType[attr]);
            }
            errorMessages.push(errText)
          }
        }
      }
    }
    return errorMessages;
  };

  public getRepeatErrorKeys(): string[] {
    return this.formArray.errors ? Object.keys(this.formArray.errors) : [];
  }

  private resolveConfiguredValidators(): ValidatorFn[] {
    const validations = this.normalizeValidationList(this.field.validators?.validation);

    return validations.flatMap((validation) => {
      if (typeof validation === 'function') {
        return [validation as ValidatorFn];
      }

      const validatorOption = this.formlyConfig.getValidator(validation);
      if (!validatorOption?.validation) {
        return [];
      }

      return [validatorOption.validation as ValidatorFn];
    });
  }

  private resolveConfiguredAsyncValidators(): AsyncValidatorFn[] {
    const validations = this.normalizeValidationList(this.field.asyncValidators?.validation);

    return validations.flatMap((validation) => {
      if (typeof validation === 'function') {
        return [validation as AsyncValidatorFn];
      }

      const validatorOption = this.formlyConfig.getValidator(validation);
      if (!validatorOption?.validation) {
        return [];
      }

      return [validatorOption.validation as AsyncValidatorFn];
    });
  }

  private normalizeValidationList(validation: unknown): Array<string | ValidatorFn | AsyncValidatorFn> {
    if (!validation) {
      return [];
    }

    return Array.isArray(validation) ? validation : [validation as string | ValidatorFn | AsyncValidatorFn];
  }

  private composeValidators(existingValidator: ValidatorFn | null, validators: ValidatorFn[]): ValidatorFn | null {
    const composedValidators = [existingValidator, ...validators].filter((validator): validator is ValidatorFn => !!validator);
    return composedValidators.length > 0 ? Validators.compose(composedValidators) : null;
  }

  private composeAsyncValidators(existingValidator: AsyncValidatorFn | null, validators: AsyncValidatorFn[]): AsyncValidatorFn | null {
    const composedValidators = [existingValidator, ...validators].filter((validator): validator is AsyncValidatorFn => !!validator);
    return composedValidators.length > 0 ? Validators.composeAsync(composedValidators) : null;
  }

}
